import { DragDropContext } from '@hello-pangea/dnd';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { HoverPanel } from '@/components/HoverPanel';
import { TimesheetControls } from '@/components/TimesheetControls';
import { ViewTable } from '@/configs/ViewTable';
import { useYaMetrika } from '@/entities/yaMetrika';
import { CurrentUserOnlyGuard } from '@/features/CurrentUserOnlyGuard';
import { DraftIndicator, DraftTimelogProvider, DraftWarningBanner } from '@/features/draftTimelogs';
import { FavoriteUsersProvider } from '@/features/favoriteUsers';
import { GlobalModals } from '@/features/GlobalModals';
import { GlobalUserFilterProvider } from '@/features/globalUserFilter';
import { useIsLogsReadOnly } from '@/features/logsMode';
import { modalManagerStore } from '@/features/ModalManagerStore';
import { ReadonlyGuard } from '@/features/ReadonlyGuard';
import { SelectUser } from '@/features/selectUser';
import { TaskStoreProvider, useTaskStore } from '@/features/Task';
import { TimeStatsProvider } from '@/features/TimeStatsModel';
import { viewSettingsStore } from '@/features/viewSettings';
import { useDrag } from '@/hooks/useDrag';
import { AnalyticsEvent } from '@/types/yametrika';
import { withProvider } from '@/utils/withProvider';
import { FavoriteTasks } from '@/widgets/FavoriteTasks';
import { TaskCalendar } from '@/widgets/TaskCalendar';
import { TaskTable } from '@/widgets/TaskTable';

import { TimeLogStoreProvider, useTimeLogStore } from './model';
import { AddLogButton, FilterButton, FoldButton, SettingsButton, StatsButton } from './ui/Buttons';
import { ModeSwitcher } from './ui/ModeSwitcher';

import s from './TimesheetsPage.module.scss';

const TimesheetsPage = observer(() => {
  const { view, dndMode, setDndMode } = viewSettingsStore;
  const { dateNavigation, triggerReload } = useTimeLogStore();
  const { sendEvent } = useYaMetrika();
  const { loadFavoriteTasks } = useTaskStore();
  const {
    isDragging,
    isDraggingFromFavorites,
    isHoveringOverFavorites,
    setIsHoveringOverFavorites,
    onDragStart,
    onDragEnd,
  } = useDrag();

  useEffect(() => {
    loadFavoriteTasks();
  }, []);

  const todayDate = DateTime.local();
  const isLogsReadOnly = useIsLogsReadOnly();

  useHotkeys(
    'T',
    (e) => {
      e.preventDefault();
      modalManagerStore.open('addLog', { date: todayDate, hasPreselectedDate: false });
      sendEvent(AnalyticsEvent.useHotkeys);
    },
    { enabled: !isLogsReadOnly },
  );

  useHotkeys('shift+T', (e) => {
    e.preventDefault();
    modalManagerStore.open('stats');
    sendEvent(AnalyticsEvent.useHotkeys);
  });

  return (
    <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className={s.timesheets}>
        <div className={s.timesheets__header}>
          <TimesheetControls
            dateNavigation={dateNavigation}
            syncConfig={{ onSync: triggerReload, tooltip: 'Обновить' }}
            viewerChildren={
              <>
                {view === ViewTable.Month && <FoldButton />}
                <SettingsButton />
                {view === ViewTable.Week && (
                  <ReadonlyGuard>
                    <ModeSwitcher value={dndMode} onChange={setDndMode} />
                  </ReadonlyGuard>
                )}
              </>
            }
            leftActionsChildren={
              <>
                <SelectUser />
                <FilterButton />
              </>
            }
            actionsChildren={
              <>
                <StatsButton />
                <div className={s.timesheets__add_column}>
                  <CurrentUserOnlyGuard>
                    <DraftIndicator />
                  </CurrentUserOnlyGuard>
                  <ReadonlyGuard>
                    <AddLogButton />
                  </ReadonlyGuard>
                </div>
              </>
            }
          />
        </div>
        <DraftWarningBanner />
        {view === ViewTable.Week ? (
          <TaskTable isDropDisabled={isDraggingFromFavorites && isHoveringOverFavorites} />
        ) : (
          <TaskCalendar isDropDisabled={isDraggingFromFavorites && isHoveringOverFavorites} />
        )}
        <GlobalModals />
      </div>

      <ReadonlyGuard>
        <HoverPanel
          isDragging={isDragging}
          onMouseEnter={() => setIsHoveringOverFavorites(true)}
          onMouseLeave={() => setIsHoveringOverFavorites(false)}
        >
          <FavoriteTasks isDragging={isDraggingFromFavorites} />
        </HoverPanel>
      </ReadonlyGuard>
    </DragDropContext>
  );
});

export default withProvider(TimesheetsPage, [
  FavoriteUsersProvider,
  GlobalUserFilterProvider,
  TimeLogStoreProvider,
  DraftTimelogProvider,
  TimeStatsProvider,
  TaskStoreProvider,
]);
